import {hasParent} from "./dom";

/**
 * Attaches {listener} to {event} on {el} and executes it once and removes it after
 * @param {HTMLElement} el Element to listen on
 * @param {string} event Event to listen for
 * @param {function} listener Listener to execute
 */
export function once(el, event, listener) {
    const _cb = (...args) => {

        listener.call(null, ...args);
        el.removeEventListener(event, _cb);
    }

    el.addEventListener(event, _cb)
}

/**
 * Caches event listener references
 * @type {Map<HTMLElement, Map<function, function>>}
 */
const listenerMap = new Map();

/**
 * @overload
 * @param {HTMLElement|HTMLElement[]} el
 * @param {string} event
 * @param {function} callback
 * @return {function}
 */
/**
 * @overload
 * @param {HTMLElement|HTMLElement[]} el
 * @param {string} event
 * @param {string} selector
 * @param {function} callback
 * @return {function}
 */

/**
 * Registers an event listener for {event} on {el}
 *
 * @param {HTMLElement|HTMLElement[]} el
 * @param {string} event
 * @param {[string|function, function|undefined]} rest
 * @return {function} A function to remove the attached listeners
 */
export function on(el, event, ...rest) {
    let selector, callback;

    if (rest.length === 2) {
        [selector, callback] = rest;
    } else {
        [callback] = rest;
    }

    const events = event.split(/\s+/);

    const _listener = !selector ? callback : (e) => {
        if (e.target.closest(selector)) {
            callback.call(null, e);
        }
    };

    const _remove = [];

    events.forEach((event) => {
        el.forEach((element) => {
            element.addEventListener(event, _listener);

            registerListener(element, callback, _listener);

            _remove.push([element, event, _listener]);
        });
    });

    return () => {
        _remove.forEach(([element, event, listener]) => {
            unregisterListener(element, callback);
            element.removeEvent(event, listener);
        });
    };
}


/**
 * Registers a listener to listener map
 * @param el
 * @param originalListener
 * @param listener
 */
function registerListener(el, originalListener, listener) {
    if (!listenerMap.has(el)) {
        listenerMap.set(el, new Map());
    }

    const listeners = listenerMap.get(el);

    listeners.set(originalListener, listener);
}

/**
 * Unregisters a listener from listener map
 *
 * @param el
 * @param originalListener
 */
function unregisterListener(el, originalListener) {
    listenerMap.get(el)?.remove(originalListener);
}

/**
 * Wraps event listener and only executes {callback} when event target has a parent that matches {selector}
 * @param {string} element
 * @param {(e: MouseEvent) => any} callback
 * @return {(function(*): void)|*}
 */
export function filterEventSelector(selector, callback) {
    return (e) => {
        const parent = e.target.matches(selector) ? e.target : e.target.closest(selector)
        console.log("filterEventSelector", {selector, e, parent});
        if (parent) {
            console.log("filterEventSelector.matche", {selector, parent});
            return callback(e);
        }
    };
}

/**
 * Wraps event listener and only executes {callback} when event target has a parent {element}
 * @param {HTMLElement} element
 * @param {(e: MouseEvent) => any} callback
 * @return {(function(*): void)|*}
 */
export function filterEventElement(element, callback) {
    return (e) => {
        if (element === e.target || hasParent(element, e.target)) {
            callback({...e, currentTarget: element});
        }
    };
}

